import { Accordion, AccordionItem } from '@heroui/react';
import React from 'react';

import {
  MAX_CATEGORIES_PER_ORDER_GUIDE,
  MAX_CATEGORY_LENGTH,
  MAX_ITEMS_PER_ORDER_GUIDE,
  MAX_PAR_LENGTH,
  MAX_ROWS_PER_GROUP,
} from '../utils/validation';

const COLUMN_DOCS: { column: string; description: string }[] = [
  {
    column: 'chat_id',
    description: 'UUID of the chat the products belong to. Required.',
  },
  {
    column: 'order_guide_id',
    description:
      'Which order guide of the chat to update. Required when the chat has more than one order guide; otherwise optional.',
  },
  {
    column: 'category',
    description: `Order guide category. Max ${MAX_CATEGORY_LENGTH} characters. Optional.`,
  },
  {
    column: 'product_id',
    description:
      "Catalog product code, used to match the distributor's catalog. Leave empty to create a custom product.",
  },
  { column: 'product_name', description: 'Product name. Required.' },
  {
    column: 'unit',
    description: 'Must exactly match the unit in the catalog. Required.',
  },
  {
    column: 'par',
    description: `Par level. Max ${MAX_PAR_LENGTH} characters. Optional.`,
  },
  {
    column: 'currency',
    description:
      'ISO currency code (EUR, USD, GBP, …). Required when price is set.',
  },
  {
    column: 'price',
    description:
      'Price of one ordering unit in cents — whole number, no "," or ".". Optional.',
  },
  { column: 'display_name', description: 'Buyer-facing name. Optional.' },
];

/** Collapsed-by-default reference for the CSV format — lives below the fold */
export const UploadInstructions: React.FC = () => (
  <Accordion variant="bordered">
    <AccordionItem
      key="csv-format"
      aria-label="CSV format and limits"
      title={<span className="text-sm font-medium">CSV format & limits</span>}
      subtitle={
        <span className="text-xs text-default-400">
          One row per product; rows are grouped by chat_id + order_guide_id and
          each group is uploaded as one batch.
        </span>
      }
    >
      <div className="space-y-4 pb-2">
        <dl className="grid grid-cols-[8.5rem_1fr] gap-x-4 gap-y-1.5">
          {COLUMN_DOCS.map(({ column, description }, index) => (
            <React.Fragment key={`${column}_${index}`}>
              <dt className="font-mono text-xs text-foreground pt-0.5">
                {column}
              </dt>
              <dd className="text-xs text-default-500">{description}</dd>
            </React.Fragment>
          ))}
        </dl>
        <p className="text-xs text-default-400">
          Limits: max {MAX_ROWS_PER_GROUP} products per order guide per upload,
          max {MAX_ITEMS_PER_ORDER_GUIDE} products in an order guide in total
          (including products existing before the upload), and max{' '}
          {MAX_CATEGORIES_PER_ORDER_GUIDE} distinct categories per order guide.
        </p>
      </div>
    </AccordionItem>
  </Accordion>
);
